import React, { useEffect, useState, useCallback } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useLanguage } from "../context/LanguageContext";
import { useHistory } from "../context/HistoryContext";
import { useFiatMarket } from "../context/FiatMarketContext";
import { calculateProfit, calculateRiskScenarios, CALC_TYPES, PERIODS } from "../utils/calculations";
import { exportToPDF, exportToExcel } from "../utils/exportUtils";
import useAnimatedNumber from "../hooks/useAnimatedNumber";
import {
    LuCheck,
    LuX,
    LuInfo,
    LuTriangleAlert,
    LuTrendingUp,
    LuTrendingDown,
    LuPiggyBank,
    LuWallet,
    LuCalculator,
    LuZap,
    LuGlobe
} from "react-icons/lu";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

// Animated Value Component
function AnimatedValue({ value, formatter }) {
    const animatedValue = useAnimatedNumber(value, 800);
    return <span className="animated-value">{formatter(animatedValue)}</span>;
}

// Toast Component
function Toast({ toast, onClose }) {
    if (!toast) return null;
    const icons = {
        success: <LuCheck />,
        error: <LuX />,
        info: <LuInfo />,
        warning: <LuTriangleAlert />
    };
    return (
        <div className={`toast ${toast.type}`}>
            <span className="toast-icon">{icons[toast.type]}</span>
            <div className="toast-content">
                <div className="toast-title">{toast.title}</div>
                <div className="toast-message">{toast.message}</div>
            </div>
            <button className="toast-close" onClick={onClose}><LuX /></button>
        </div>
    );
}

// Currency Selector Component
function CurrencySelector({ currencies, selectedCurrency, onSelect, lang }) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filtered = currencies.filter(c =>
        c.code.toLowerCase().includes(search.toLowerCase()) ||
        c.name.toLowerCase().includes(search.toLowerCase())
    );

    const selected = currencies.find(c => c.symbol === selectedCurrency);

    return (
        <div className="currency-selector">
            <button
                className="currency-selector-btn"
                onClick={() => setIsOpen(!isOpen)}
            >
                {selected ? (
                    <>
                        <LuGlobe className="currency-icon" />
                        <span className="currency-code">{selected.code}</span>
                        <span className="currency-price">₺{selected.forexSell?.toFixed(4)}</span>
                    </>
                ) : (
                    <span>{lang === 'tr' ? 'Döviz Seçin' : 'Select Currency'}</span>
                )}
            </button>
            {isOpen && (
                <div className="currency-dropdown">
                    <input
                        type="text"
                        className="currency-search"
                        placeholder={lang === 'tr' ? 'Döviz ara...' : 'Search currency...'}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />
                    <div className="currency-list">
                        {filtered.map(currency => (
                            <button
                                key={currency.symbol}
                                className={`currency-item ${selectedCurrency === currency.symbol ? 'active' : ''}`}
                                onClick={() => {
                                    onSelect(currency.symbol);
                                    setIsOpen(false);
                                    setSearch('');
                                }}
                            >
                                <LuGlobe className="currency-icon" />
                                <div className="currency-info">
                                    <span className="currency-code">{currency.code}</span>
                                    <span className="currency-name">{currency.name}</span>
                                </div>
                                <span className="currency-price">₺{currency.forexSell?.toFixed(4)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function FiatHome() {
    const { lang, t } = useLanguage();
    const { addToHistory } = useHistory();
    const { forex, connected } = useFiatMarket();

    // Form State
    const [selectedForex, setSelectedForex] = useState('USD');
    const [forexAmount, setForexAmount] = useState(1000);
    const [balance, setBalance] = useState(10000);
    const [investmentRatio, setInvestmentRatio] = useState(100);
    const [dailyPercent, setDailyPercent] = useState(0.1);
    const [targetDay, setTargetDay] = useState(365);
    const [calculationType, setCalculationType] = useState(CALC_TYPES.COMPOUND);
    const [period, setPeriod] = useState(PERIODS.MONTHLY);

    // Get selected forex live price
    const currentForex = forex.find(f => f.symbol === selectedForex);
    const livePrice = currentForex?.forexSell || 0;

    // Update balance when forex or amount changes
    useEffect(() => {
        if (livePrice > 0) {
            setBalance(forexAmount * livePrice);
        }
    }, [selectedForex, forexAmount, livePrice]);

    // Results
    const [results, setResults] = useState([]);
    const [stats, setStats] = useState(null);
    const [riskScenarios, setRiskScenarios] = useState([]);

    // UI State
    const [toast, setToast] = useState(null);

    const formatTRY = useCallback((value) => {
        return `₺${(value || 0).toLocaleString('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    }, []);

    const showToast = (type, title, message) => {
        setToast({ type, title, message });
        setTimeout(() => setToast(null), 3000);
    };

    const handleCalculate = useCallback(() => {
        const balanceValue = Number(balance);
        if (!balanceValue || balanceValue <= 0) {
            showToast('warning',
                lang === 'tr' ? 'Uyarı' : 'Warning',
                lang === 'tr' ? 'Lütfen döviz seçin ve miktar girin.' : 'Please select currency and enter amount.'
            );
            return;
        }

        const params = {
            balance: balanceValue,
            investmentRatio: Number(investmentRatio),
            percentage: Number(dailyPercent),
            targetDay: Number(targetDay),
            calcType: calculationType,
            period
        };

        const data = calculateProfit(params);
        const scenarios = calculateRiskScenarios(params);

        const calculatedStats = {
            initialBalance: params.balance,
            finalBalance: data[data.length - 1]?.usdt || params.balance,
            totalProfit: (data[data.length - 1]?.usdt || params.balance) - params.balance,
            dailyAverage: data.length > 0 ? data.reduce((sum, d) => sum + d.profit, 0) / data.length : 0,
            profitRate: params.balance > 0 ? ((data[data.length - 1]?.usdt || params.balance) - params.balance) / params.balance * 100 : 0
        };

        setResults(data.map(d => ({
            ...d,
            try: d.usdt
        })));
        setStats(calculatedStats);
        setRiskScenarios(scenarios);

        // Add to history
        addToHistory({
            params,
            stats: calculatedStats,
            currency: 'TRY',
            mode: 'fiat',
            forex: currentForex ? {
                symbol: currentForex.symbol,
                code: currentForex.code,
                name: currentForex.name,
                price: livePrice,
                amount: forexAmount
            } : null
        });

        showToast('success', t('toast.calculated'), t('toast.calculatedDesc'));
    }, [balance, investmentRatio, dailyPercent, targetDay, calculationType, period, addToHistory, t, currentForex, livePrice, forexAmount, lang]);

    // Chart data - Fiat style (blue/green)
    const chartData = {
        labels: results.map(r => `${r.day}`),
        datasets: [
            {
                label: 'TRY',
                data: results.map(r => r.usdt),
                borderColor: '#2563eb',
                backgroundColor: 'rgba(37, 99, 235, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: '#2563eb',
                borderWidth: 2
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#1e293b',
                bodyColor: '#1e293b',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                callbacks: {
                    label: (context) => formatTRY(context.raw)
                }
            }
        },
        scales: {
            x: {
                grid: { color: 'rgba(0, 0, 0, 0.05)' },
                ticks: { color: 'rgba(0, 0, 0, 0.5)' }
            },
            y: {
                grid: { color: 'rgba(0, 0, 0, 0.05)' },
                ticks: {
                    color: 'rgba(0, 0, 0, 0.5)',
                    callback: (value) => formatTRY(value)
                }
            }
        }
    };

    return (
        <div className="fiat-home">
            <div className="page-header home-header">
                <div className="page-header-content">
                    <div className="page-title-group">
                        <div>
                            <h1 className="page-title">{lang === 'tr' ? 'Fiat Getiri Hesaplama' : 'Fiat Profit Calculator'}</h1>
                        </div>
                    </div>
                    {connected && (
                        <div className="page-actions">
                            <span className="live-indicator tcmb">
                                <LuZap className="live-icon" />
                                <span>TCMB</span>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="calculator-grid">
                {/* Form Card */}
                <div className="card form-card">
                    <div className="card-header">
                        <h2><LuCalculator /> {lang === 'tr' ? 'Hesaplama Parametreleri' : 'Calculation Parameters'}</h2>
                        {connected && (
                            <span className="live-badge tcmb">
                                <span className="live-dot"></span>
                                TCMB
                            </span>
                        )}
                    </div>

                    {/* Currency Selection */}
                    <div className="coin-selector-section">
                        <div className="form-group coin-select-group">
                            <label className="form-label">{lang === 'tr' ? 'Döviz Seçin' : 'Select Currency'}</label>
                            <CurrencySelector
                                currencies={forex}
                                selectedCurrency={selectedForex}
                                onSelect={setSelectedForex}
                                lang={lang}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{lang === 'tr' ? 'Miktar' : 'Amount'}</label>
                            <input
                                type="number"
                                className="form-input"
                                value={forexAmount}
                                onChange={(e) => setForexAmount(Number(e.target.value))}
                                min="0"
                                step="100"
                            />
                        </div>
                        {currentForex && (
                            <div className="coin-live-info fiat-live-info">
                                <div className="coin-price-display">
                                    <LuGlobe className="forex-icon-large" />
                                    <div className="coin-price-info">
                                        <span className="coin-current-price">₺{livePrice.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
                                        <span className="forex-name">{currentForex.name}</span>
                                    </div>
                                </div>
                                <div className="coin-total-value">
                                    <span className="total-label">{lang === 'tr' ? 'Toplam Değer (TRY)' : 'Total Value (TRY)'}</span>
                                    <span className="total-value">₺{(forexAmount * livePrice).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label className="form-label">{t('form.balance')} (TRY)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={(balance || 0).toFixed(2)}
                                readOnly
                                disabled
                            />
                            <span className="form-hint">{lang === 'tr' ? 'Seçilen döviz miktarından otomatik hesaplanır' : 'Auto-calculated from selected currency amount'}</span>
                        </div>
                        <div className="form-group">
                            <label className="form-label">{t('form.investmentRatio')} (%)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={investmentRatio}
                                onChange={(e) => setInvestmentRatio(e.target.value)}
                                min="0"
                                max="100"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{lang === 'tr' ? 'Faiz Oranı' : 'Interest Rate'} (%)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={dailyPercent}
                                onChange={(e) => setDailyPercent(e.target.value)}
                                step="0.01"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{lang === 'tr' ? 'Vade (Gün)' : 'Term (Days)'}</label>
                            <input
                                type="number"
                                className="form-input"
                                value={targetDay}
                                onChange={(e) => setTargetDay(e.target.value)}
                                min="1"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{t('form.calculationType')}</label>
                            <select
                                className="form-select"
                                value={calculationType}
                                onChange={(e) => setCalculationType(e.target.value)}
                            >
                                <option value={CALC_TYPES.COMPOUND}>{t('form.compound')}</option>
                                <option value={CALC_TYPES.SIMPLE}>{t('form.simple')}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">{lang === 'tr' ? 'Faiz Periyodu' : 'Interest Period'}</label>
                            <select
                                className="form-select"
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                            >
                                <option value={PERIODS.DAILY}>{t('form.daily')}</option>
                                <option value={PERIODS.WEEKLY}>{t('form.weekly')}</option>
                                <option value={PERIODS.MONTHLY}>{t('form.monthly')}</option>
                            </select>
                        </div>
                    </div>
                    <button className="btn btn-primary btn-calculate" onClick={handleCalculate}>
                        <LuCalculator /> {t('form.calculate')}
                    </button>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="stats-grid">
                        <div className="stat-card fiat-stat">
                            <div className="stat-icon"><LuTrendingUp /></div>
                            <div className="stat-content">
                                <span className="stat-label">{lang === 'tr' ? 'Toplam Kâr' : 'Total Profit'}</span>
                                <AnimatedValue value={stats.totalProfit} formatter={formatTRY} />
                            </div>
                        </div>
                        <div className="stat-card fiat-stat">
                            <div className="stat-icon"><LuWallet /></div>
                            <div className="stat-content">
                                <span className="stat-label">{lang === 'tr' ? 'Son Bakiye' : 'Final Balance'}</span>
                                <AnimatedValue value={stats.finalBalance} formatter={formatTRY} />
                            </div>
                        </div>
                        <div className="stat-card fiat-stat">
                            <div className="stat-icon"><LuPiggyBank /></div>
                            <div className="stat-content">
                                <span className="stat-label">{lang === 'tr' ? 'Kâr Oranı' : 'Profit Rate'}</span>
                                <AnimatedValue value={stats.profitRate} formatter={(v) => `%${(v || 0).toFixed(2)}`} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Chart Card */}
                {results.length > 0 && (
                    <div className="card chart-card">
                        <div className="card-header">
                            <h2><LuTrendingUp /> {lang === 'tr' ? 'Bakiye Grafiği' : 'Balance Chart'}</h2>
                        </div>
                        <div className="chart-container" style={{ height: '300px' }}>
                            <Line data={chartData} options={chartOptions} />
                        </div>
                    </div>
                )}

                {/* Results Table */}
                {results.length > 0 && (
                    <div className="card results-card">
                        <div className="card-header">
                            <h2>{lang === 'tr' ? 'Vade Detayları' : 'Term Details'}</h2>
                            <div className="card-actions">
                                <button className="btn btn-sm" onClick={() => exportToPDF(results, stats, lang)}>PDF</button>
                                <button className="btn btn-sm" onClick={() => exportToExcel(results, stats, lang)}>Excel</button>
                            </div>
                        </div>
                        <div className="table-container">
                            <table className="results-table">
                                <thead>
                                    <tr>
                                        <th>{lang === 'tr' ? 'Gün' : 'Day'}</th>
                                        <th>{lang === 'tr' ? 'Bakiye' : 'Balance'} (TRY)</th>
                                        <th>{lang === 'tr' ? 'Faiz Getirisi' : 'Interest'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.slice(0, 30).map((row) => (
                                        <tr key={row.day}>
                                            <td>{row.day}</td>
                                            <td className="text-accent">{formatTRY(row.usdt)}</td>
                                            <td className="text-green">+{formatTRY(row.profit)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Risk Scenarios */}
                {riskScenarios.length > 0 && (
                    <div className="card risk-card">
                        <div className="card-header">
                            <h2><LuTriangleAlert /> {lang === 'tr' ? 'Senaryo Analizi' : 'Scenario Analysis'}</h2>
                        </div>
                        <div className="risk-scenarios">
                            {riskScenarios.map((scenario, index) => (
                                <div key={index} className={`risk-scenario risk-${scenario.level}`}>
                                    <div className="risk-label">{scenario.name}</div>
                                    <div className="risk-value">{formatTRY(scenario.finalBalance)}</div>
                                    <div className="risk-change">
                                        {(scenario.profitPercent || 0) >= 0 ? <LuTrendingUp /> : <LuTrendingDown />}
                                        %{(scenario.profitPercent || 0).toFixed(2)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
        </div>
    );
}

export default FiatHome;
